import React, { useId } from 'react';
import {
  BOARD_LANGUAGES,
  type LocalizedText,
  type BoardValidationIssueDto,
  type AdminBoardEditorialDto,
} from '@living-library/contracts';
import { resources } from '../../../app/src/i18n/resources';

export const c = resources.en.translation.boardAdmin;
export const button = 'admin-button';
export const input = 'admin-input mt-2 block w-full';
export const card = 'admin-panel p-5 sm:p-6';
export const emptyText = (): LocalizedText => ({ sk: '', cs: '', en: '' });

export function LanguageEditor({
  label,
  value,
  limit,
  onChange,
  multiline = false,
  disabled = false,
}: {
  label: string;
  value: LocalizedText;
  limit: number;
  onChange(value: LocalizedText): void;
  multiline?: boolean;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <fieldset disabled={disabled} className="my-5 min-w-0 border-t border-line/65 pt-4">
      <legend className="pr-3 text-sm font-semibold">{label}</legend>
      <div className="grid gap-3 lg:grid-cols-3">
        {BOARD_LANGUAGES.map((language) => {
          const count = Array.from(value[language].trim()).length,
            missing = !value[language].trim() || count > limit;
          return (
            <label
              key={language}
              className="block min-w-0 pt-2 text-xs font-semibold tracking-wide"
              htmlFor={`${id}-${language}`}
            >
              {language.toUpperCase()}{' '}
              <span className={missing ? 'text-warning' : 'text-safe'}>{missing ? '!' : '✓'}</span>
              <textarea
                id={`${id}-${language}`}
                className={input}
                rows={multiline ? 4 : 2}
                value={value[language]}
                aria-invalid={missing}
                aria-describedby={`${id}-${language}-count`}
                onChange={(event) => onChange({ ...value, [language]: event.target.value })}
              />
              <span
                id={`${id}-${language}-count`}
                className={`mt-2 block text-right text-[11px] font-normal tabular-nums ${count > limit ? 'text-warning' : 'text-ink-muted'}`}
              >
                {count} / {limit}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
export function Completeness({ issues }: { issues: BoardValidationIssueDto[] }) {
  return (
    <span className="flex gap-3 text-xs">
      {BOARD_LANGUAGES.map((language) => {
        const missing = issues.some((issue) => issue.language === language);
        return (
          <span className={missing ? 'text-warning' : 'text-safe'} key={language}>
            {language.toUpperCase()} {missing ? '!' : '✓'}
          </span>
        );
      })}
    </span>
  );
}
export function Status({ value }: { value: AdminBoardEditorialDto['status'] }) {
  return (
    <span
      className={`admin-status ${value === 'published' || value === 'active' ? 'bg-safe-fill text-safe' : 'bg-canvas text-brass-pale'}`}
    >
      {c.statuses[value]}
    </span>
  );
}
export function Requirements({
  issues,
  dirty,
}: {
  issues: BoardValidationIssueDto[];
  dirty?: boolean;
}) {
  return issues.length || dirty ? (
    <div
      className="my-5 rounded-xl border border-warning/25 bg-danger-fill/35 p-4 text-sm leading-6 text-warning"
      role="status"
    >
      <strong>{c.requirements}</strong>
      {dirty && <p>{c.unsaved}</p>}
      <ul className="mt-2 list-inside list-disc">
        {issues.map((issue, index) => (
          <li key={index}>{issue.message}</li>
        ))}
      </ul>
    </div>
  ) : null;
}
